import { Icon, ICONS } from 'components/Icon';
import { Txt } from 'components/Txt';
import { differenceInCalendarDays, parseISO } from 'date-fns';
import { useScreenSize } from 'hooks/useScreenSize';
import { pluralise } from 'lib/pluralise';
import { theme } from 'lib/theme';
import { orderBy } from 'lodash';
import * as React from 'react';
import { StyleSheet, View } from 'react-native';
import { AssetListItemProps } from 'screens/AssetsScreen/AssetList/Item/types';
import { Organisation } from 'store/organisations/types';

const colorMute = '333333';

type AssessmentMeta = {
  name: string;
  dueDays: number | undefined;
  dueDaysString: string | undefined;
  statusString: string | undefined;
  color: string;
};

const iconSize = 24;

const formatDaysString = (days: number): string => {
  return days === 0
    ? 'Today'
    : days < 0
      ? pluralise(-days, 'Overdue {x} Day', 'Overdue {x} Days')
      : pluralise(days, 'Due {x} Day', 'Due {x} Days');
};

const getAssessmentStatusColor = (
  statusId: string,
  statuses: Organisation['assessment_statuses'],
): string =>
  statusId === 'notstarted'
    ? `#${colorMute}`
    : `#${statuses[statusId]?.colour || colorMute}`;

export const ItemMeta: React.FC<AssetListItemProps> = ({
  asset,
  organisation,
}) => {
  const screenSize = useScreenSize(true);
  const complianceSummary = asset.compliance_summary;
  const dateNow = new Date();

  const assessmentsByStatus = Object.entries(complianceSummary).reduce<
    Record<string, AssessmentMeta[]>
  >(
    (acc, [complianceSubtypeId, subtype]) => {
      const complianceSubtype =
        organisation.compliance_subtypes[complianceSubtypeId];
      if (
        complianceSubtype &&
        acc[subtype.status_id] &&
        (subtype.assigned || subtype.status_id !== 'notstarted')
      ) {
        const dueDays = subtype.due_date
          ? differenceInCalendarDays(parseISO(subtype.due_date), dateNow)
          : undefined;
        const dueDaysString =
          dueDays !== undefined ? formatDaysString(dueDays) : undefined;
        const statusString =
          subtype.status_id !== 'notstarted'
            ? organisation.assessment_statuses[subtype.status_id]!.name
            : undefined;
        acc[subtype.status_id as keyof typeof assessmentsByStatus]!.push({
          color:
            subtype.status_id === 'notstarted' && dueDays && dueDays < 0
              ? theme.colors.red
              : getAssessmentStatusColor(
                  subtype.status_id,
                  organisation.assessment_statuses,
                ),
          dueDays,
          dueDaysString,
          name: complianceSubtype.name || 'Unknown',
          statusString,
        });
      }
      return acc;
    },
    {
      inprogress: [],
      notstarted: [],
      submitted: [],
    },
  );

  const assessments = [
    ...orderBy(assessmentsByStatus.notstarted!, ['dueDays']),
    ...orderBy(assessmentsByStatus.inprogress!, ['dueDays']),
    // Only show submitted when there's no notstarted or inprogress
    ...(!assessmentsByStatus.notstarted!.length &&
    !assessmentsByStatus.inprogress!.length
      ? assessmentsByStatus.submitted!
      : []),
  ];

  return (
    <View
      style={
        screenSize === 'small'
          ? styles.metaContainerSmall
          : styles.metaContainer
      }
    >
      {/** Tasks Information */}
      {/* <View style={styles.metaItem}>
          <Icon
            source={ICONS.ListCheckLight}
            size={iconSize}
            color={colorMute}
            style={styles.metaItemIcon}
          />
          <View style={styles.metaItemContent}>
            <Text
              bold
              small
              style={[styles.metaItemTextLine1, { color: colorMute }]}
              numberOfLines={1}
            >
              5 Tasks
            </Text>
            <Text
              small
              style={[styles.metaItemTextLine2, { color: colorMute }]}
              numberOfLines={1}
            >
              Due 2 Days
            </Text>
          </View>
        </View> */}

      {assessments.length > 1 ? (
        <View style={styles.metaItem} key="assessments">
          <Icon
            key={`assessment-icon-${assessments[0]!.name}`}
            source={ICONS.FileDuotone}
            size={iconSize}
            color={assessments[0]!.color}
            style={styles.metaItemIcon}
          />
          <View style={styles.metaItemContent}>
            {(assessments.length > 2 ? [assessments[0]!] : assessments).map(
              (item, index) => (
                <Txt
                  key={`assessment-${item.name}`}
                  size="xsmall"
                  weight="medium"
                  style={[
                    !index
                      ? styles.metaItemTextLine1
                      : styles.metaItemTextLine2,
                    { color: item.color },
                  ]}
                  numberOfLines={1}
                >
                  {item.name}
                </Txt>
              ),
            )}
            {assessments.length > 2 && (
              <Txt
                key={`assessment-${assessments[0]!.name}`}
                size="xsmall"
                weight="medium"
                style={[styles.metaItemTextLine2, { color: `#${colorMute}` }]}
                numberOfLines={1}
              >
                {`Plus ${assessments.length - 1} Others...`}
              </Txt>
            )}
          </View>
        </View>
      ) : (
        assessments.map((item) => (
          <View style={styles.metaItem} key={`assessment-${item.name}`}>
            <Icon
              source={ICONS.FileDuotone}
              size={iconSize}
              color={item.color}
              style={styles.metaItemIcon}
            />
            <View style={styles.metaItemContent}>
              <Txt
                size="xsmall"
                weight="medium"
                style={[styles.metaItemTextLine1, { color: item.color }]}
                numberOfLines={1}
              >
                {item.name}
              </Txt>
              <Txt
                size="xsmall"
                weight="medium"
                style={[styles.metaItemTextLine2, { color: item.color }]}
                numberOfLines={1}
              >
                {item.statusString || item.dueDaysString}
              </Txt>
            </View>
          </View>
        ))
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  metaContainer: {
    borderRadius: 4,
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginLeft: -theme.padding / 2,
  },
  metaContainerSmall: {
    borderRadius: 4,
    flexDirection: 'row',
    marginLeft: -theme.padding / 2,
  },
  metaItem: {
    alignItems: 'center',
    flexDirection: 'row',
    paddingHorizontal: theme.padding / 2,
    paddingVertical: theme.grid / 4,
  },
  metaItemContent: {
    flexShrink: 1,
    minHeight: 19,
  },
  metaItemIcon: {
    marginRight: 6,
  },
  metaItemTextLine1: {
    // marginBottom: -1,
    width: 150,
  },
  metaItemTextLine2: {
    // marginTop: -1,
    width: 150,
  },
});
